import React, {useState} from "react";
import "focus-visible";
import {
  getFont,
  styled,
  useTheme,
  Theme,
  Brands,
  useEnabledFeatures,
  useInverseStyleContext,
  InverseProps,
  forBrands,
} from "@core-ui/react-stitch";
import {CSSObject} from "@emotion/react";
import {CircleSwatch} from "./circle-swatch";
import {SwatchContainer} from "./swatch-container";
import {StockIndicator} from "./stock-indicator";
import {BackOrderMessage} from "./backorder-message";
import {ColorSwatchProps} from "../types";
import {outline} from "./swatch-outlines";
import {SquareSwatch} from "./square-swatch";

const {useLocalize} =
  typeof __USE_I18N__ === "undefined"
    ? require("@core-ui/localization-provider")
    : require("@core-ui/i18n");

const SwatchWrapper = styled.div({
  display: "inline-block",
  position: "relative",
});

const SwatchAndLabelContainer = styled.div<{
  isSquare: boolean;
}>(({isSquare}) => {
  const padding = isSquare ? {} : {padding: "7px 5px"};
  return {
    ...padding,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "@media (pointer: fine)": {
      "&:hover label": {
        textDecoration: "underline",
      },
    },
    "@media (pointer: coarse)": {
      "&:active label": {
        textDecoration: "underline",
      },
    },
  };
});

const OutOfStockMessage = styled.span();

const SwatchInput = styled.input({
  opacity: 0,
  position: "absolute",
  cursor: "pointer",
  width: "100%",
  height: "100%",
  zIndex: 1,
  top: "50%",
  left: "50%",
  transform: "translate(-50%,-50%)",
  margin: 0,
});

export const getOuterBoxShadowSpread = (
  size: ColorSwatchProps["size"]
): string => (size === "small" ? "0.28rem" : "0.375rem");

type AllBrandBorderInfo = ReturnType<typeof outline>;
type BorderInfo = AllBrandBorderInfo[keyof AllBrandBorderInfo];

const makeBoxShadow = (
  styles: BorderInfo,
  selected: boolean,
  invert: boolean,
  size?: "default" | "small" | undefined
) => {
  //  Currently only changing styles for size for ON
  const sizeStyle = size === "small" ? styles.small : styles;
  const invertStyle = selected
    ? styles?.invert?.selected
    : styles?.invert?.default;
  const defaultStyle = selected ? sizeStyle.selected : sizeStyle.default;
  const style = invert ? invertStyle : defaultStyle;
  if (!style.inner.size) {
    return "";
  }
  const inner = `0 0 0 ${style.inner.size} ${style.inner.color}`;
  if (!style.outer.size) {
    return inner;
  }
  const outer = `0 0 0 ${style.outer.size} ${style.outer.color}`;
  return `${inner}, ${outer}`;
};

const makeSquareBoxShadow = (
  styles: BorderInfo,
  selected: boolean,
  invert: boolean
) => {
  const invertStyle = selected
    ? styles?.invert?.selected
    : styles?.invert?.default;

  const defaultStyle = selected ? styles.selected : styles.default;

  const style = invert ? invertStyle : defaultStyle;
  if (!style.inner.size) {
    return "";
  }
  const inner = `0 0 0 ${style.inner.size} ${style.inner.color}`;
  return `${inner}`;
};

const getBRRedesignEnabled = (enabledFeatures?: Record<string, boolean>) =>
  !!(
    enabledFeatures &&
    Object.keys(enabledFeatures).some((feature) =>
      ["cat-br-redesign", "search-br-redesign", "seo-br-redesign"].includes(
        feature
      )
    )
  );

export const getBoxShadow = (
  selected: boolean | undefined,
  size: "small" | "default" | undefined,
  theme: Theme,
  flags: Record<string, boolean>,
  invert: boolean | undefined = false
) => {
  const isBRRedesign = getBRRedesignEnabled(flags);

  const pdpCatRedesign = flags["pdp-redesign-2022"] || isBRRedesign;
  const pdpRedesign = flags["pdp-redesign-2022"];
  const isSquare = pdpRedesign || isBRRedesign;

  const isBrSelectedUnderline = pdpRedesign && !isBRRedesign;
  const isBrSelectedBorder = pdpRedesign || isBRRedesign;

  const getBRSelectedStyle = (): ReturnType<typeof outline>[
    | "brPdpRedesign"
    | "brPdpCatRedesign"
    | "br"] => {
    if (isBrSelectedUnderline) {
      return outline(theme).brPdpRedesign;
    }
    if (isBrSelectedBorder) {
      return outline(theme).brPdpCatRedesign;
    }
    return outline(theme).br;
  };

  const brandedBoxShadow = forBrands(theme, {
    at: () => ({
      boxShadow: makeBoxShadow(
        pdpCatRedesign ? outline(theme).atRedesign : outline(theme).at,
        !!selected,
        invert
      ),
    }),
    gap: () => ({
      boxShadow: makeBoxShadow(
        pdpCatRedesign ? outline(theme).gapRedesign : outline(theme).gap,
        !!selected,
        invert
      ),
    }),
    on: () => ({
      boxShadow: makeBoxShadow(outline(theme).on, !!selected, invert, size),
    }),
    br: () =>
      isSquare
        ? {
            boxShadow: makeSquareBoxShadow(
              getBRSelectedStyle(),
              !!selected,
              invert
            ),
          }
        : {
            boxShadow: makeBoxShadow(getBRSelectedStyle(), !!selected, invert),
          },
    brfs: () =>
      isSquare
        ? {
            boxShadow: makeSquareBoxShadow(
              getBRSelectedStyle(),
              !!selected,
              invert
            ),
          }
        : {
            boxShadow: makeBoxShadow(getBRSelectedStyle(), !!selected, invert),
          },
    gapfs: () => ({
      boxShadow: makeBoxShadow(
        pdpCatRedesign ? outline(theme).gapRedesign : outline(theme).gapfs,
        !!selected,
        invert
      ),
    }),
  }) as CSSObject;

  return brandedBoxShadow;
};

const SwatchLabel = styled.label<
  InverseProps & {
    crossBrand?: boolean;
  }
>(({theme, crossBrand, invert}) => ({
  ...getFont("primary")({theme, crossBrand}),
  paddingTop: "8px",
  fontSize: "0.875rem",
  textTransform: "capitalize",
  color: invert && theme.color.inverse.b1,
}));

const BottomSelector = styled.div<InverseProps & {size: string}>(
  ({theme, invert}) => ({
    marginTop: 5,
    width: "100%",
    display: "block",
    borderBottom: `solid 2px ${invert ? theme.color.inverse.b1 : ""}`,
  })
);

const shouldRenderStockIndicator = (
  shouldDisplayInStock: ColorSwatchProps["shouldDisplayInStock"],
  inStock: ColorSwatchProps["inStock"]
): boolean | undefined =>
  typeof inStock !== "undefined" && !inStock && shouldDisplayInStock;

export const ColorSwatch = ({
  backOrderMessage,
  className,
  colorName,
  crossBrand,
  customColor,
  id,
  inStock = true,
  onChange = () => {},
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  radioName,
  productImage,
  productImageAltText,
  selected = false,
  shouldDisplayInStock = true,
  showColorName = false,
  size = "default",
  type = "radio",
  url,
  value,
  link,
  "aria-describedby": ariaDescribedByProp,
  ...props
}: ColorSwatchProps): JSX.Element => {
  const {localize} = useLocalize();
  const [hover, setHover] = useState(false);
  const theme = useTheme();
  const outOfStockId = `${id}-outofstock`;
  const outOfStock = localize("fui.color_swatch.out_of_stock", {});
  const isOutOfStock = shouldRenderStockIndicator(
    shouldDisplayInStock,
    inStock
  );
  const enabledFeatures = useEnabledFeatures();
  const pdpRedesign = enabledFeatures["pdp-redesign-2022"];
  const isBRRedesign = getBRRedesignEnabled(enabledFeatures);
  const {invert} = useInverseStyleContext();

  const getAriaDescribedBy = (): string | undefined => {
    if (isOutOfStock && ariaDescribedByProp)
      return `${outOfStockId} ${ariaDescribedByProp}`;
    if (isOutOfStock) return outOfStockId;
    return ariaDescribedByProp;
  };
  const isBR = [
    Brands.BananaRepublic,
    Brands.BananaRepublicFactoryStore,
  ].includes(theme.brand);
  const {boxShadow} = getBoxShadow(
    selected,
    size,
    theme,
    enabledFeatures,
    invert
  );
  const baseSwatchProps = {
    boxShadow,
    selected,
    size,
    title: colorName,
    value,
    id,
    colorName,
    link,
  };
  const swatchProps = customColor
    ? {
        customColor,
        ...baseSwatchProps,
      }
    : {
        url,
        ...baseSwatchProps,
      };

  return (
    <SwatchWrapper>
      <SwatchAndLabelContainer isSquare={(pdpRedesign || isBRRedesign) && isBR}>
        <SwatchContainer
          className={className}
          invert={invert}
          isSquare={(pdpRedesign || isBRRedesign) && isBR}
          selected={selected}
          showColorName={showColorName}
          showUnderline={pdpRedesign && !isBRRedesign && isBR}
          size={size}
          theme={theme}
        >
          <SwatchInput
            aria-describedby={getAriaDescribedBy()}
            aria-disabled={isOutOfStock}
            aria-label={!showColorName ? colorName : undefined}
            checked={selected}
            data-alt-text={productImageAltText}
            data-url={productImage}
            id={id}
            name={radioName || "color-radio"}
            onChange={onChange}
            onMouseEnter={(event) => {
              onMouseEnter(event);
              setHover(true);
            }}
            onMouseLeave={(event) => {
              onMouseLeave(event);
              setHover(false);
            }}
            type={type}
            value={value}
            {...props}
          />
          {(pdpRedesign || isBRRedesign) && isBR ? (
            <SquareSwatch {...swatchProps} />
          ) : (
            <CircleSwatch {...swatchProps} />
          )}
          {isOutOfStock && (
            <StockIndicator enabledFeatures={enabledFeatures} invert={invert} />
          )}
          {backOrderMessage && (
            <BackOrderMessage
              aria-live="polite"
              hover={hover}
              role="alert"
              translateX={getOuterBoxShadowSpread(size)}
            >
              {backOrderMessage}
            </BackOrderMessage>
          )}
        </SwatchContainer>
        {showColorName && (
          <SwatchLabel htmlFor={id} invert={invert}>
            {link ? (
              <a
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  e.currentTarget.closest("label")?.click();
                }}
              >
                {colorName}
              </a>
            ) : (
              <>{colorName}</>
            )}
          </SwatchLabel>
        )}
        {isOutOfStock && (
          <OutOfStockMessage
            css={theme.utilities.visuallyHidden}
            id={outOfStockId}
          >
            {outOfStock}
          </OutOfStockMessage>
        )}
      </SwatchAndLabelContainer>
      {pdpRedesign && !isBRRedesign && isBR && selected && (
        <BottomSelector invert={invert} size={size} />
      )}
    </SwatchWrapper>
  );
};

export type {ColorSwatchProps} from "../types";
